/**
 * AppBouncer SDK - Main Application Entry Point
 * Ties together all verification modules into a unified API
 */

import { CameraConfig, CameraErrorInfo, CameraManager } from '../camera';
import {
  AgeVerificationResult,
  AgeVerifier,
  AgeVerifierConfig,
  LiveMultiVerifyConfig,
  MultiVerificationResult,
  MultiVerifyConfig,
} from '../age-estimation';
import { ActionResult, LivenessConfig, LivenessDetector, LivenessVerifyResult } from '../liveness';
import { CapturedDocument, DocumentCapture, DocumentConfig } from '../document';
import {
  ScreenConfig,
  ScreenName,
  UIStateConfig,
  UIStateMachine,
  VerificationMethod,
  VerificationMode,
  VerificationResult,
} from './state-machine';
import {
  checkSceneQuality,
  FaceDetectionConfig,
  FaceLandmarks,
  Landmark,
  MediaPipeFaceDetector,
  SceneQualityResult,
  FaceComparator,
  resetFaceComparator,
  type CaptureStage,
  type FaceComparisonResult,
} from '../face-detection';
import { PollingService, RequirementsData, RequirementsService } from '../verification';
import { redactSecrets } from '../utils/log-redaction';
import { getFeedbackFromQuality } from '../utils/face-quality-messages';
import { FaceDetectionLoop } from '../loops';
import { t } from '../i18n';

// ============================================================
// Types
// ============================================================

export interface AppBouncerConfig {
  /** API base URL (required) */
  apiBaseUrl: string;
  /** Assets base URL for static files like models (optional, defaults to relative paths) */
  assetsBaseUrl?: string;
  /** API key for authentication */
  apiKey?: string;
  /** Verification mode */
  mode?: VerificationMode;
  /** Camera configuration */
  camera?: Partial<CameraConfig>;
  /** Age verifier configuration */
  ageVerifier?: Partial<AgeVerifierConfig>;
  /** Liveness configuration */
  liveness?: Partial<LivenessConfig>;
  /** Document capture configuration */
  document?: Partial<DocumentConfig>;
  /** UI state configuration */
  ui?: Partial<UIStateConfig>;
  /** Face detection configuration */
  faceDetection?: Partial<FaceDetectionConfig>;
  /** Debug mode */
  debug?: boolean;
}

export interface AppBouncerCallbacks {
  /** Called when SDK is ready */
  onReady?: () => void;
  /** Called when screen changes */
  onScreenChange?: (screen: ScreenConfig, prevScreen?: ScreenName) => void;
  /** Called on verification complete */
  onComplete?: (result: VerificationResult) => void;
  /** Called on error */
  onError?: (error: Error, context?: string) => void;
  /** Called with status messages */
  onStatus?: (message: string, type: 'info' | 'success' | 'warning' | 'error') => void;
  /** Called when face is detected */
  onFaceDetected?: (detected: boolean, quality?: SceneQualityResult) => void;
  /** Called during liveness with action instructions and step progress */
  onLivenessAction?: (
    action: string,
    instruction: string,
    stepIndex?: number,
    totalSteps?: number
  ) => void;
  /** Called with age estimation result */
  onAgeEstimate?: (age: number, confidence: number) => void;
  /** Called when document is captured */
  onDocumentCapture?: (doc: CapturedDocument) => void;
  /** Called when face comparison completes (anti-spoofing check) */
  onFaceComparison?: (result: FaceComparisonResult) => void;
}

export interface LivenessActionResult {
  action: string;
  ok: boolean;
}

export interface VerificationSession {
  sessionId: string;
  challengeId?: string;
  token?: string;
  startedAt: number;
  expiresAt?: number;
  mode: VerificationMode;
  results: {
    liveness?: { passed: boolean; score?: number; actions?: LivenessActionResult[] };
    age?: { estimate: number; confidence: number };
    document?: { verified: boolean; extractedData?: Record<string, string> };
    faceMatch?: { matched: boolean; confidence?: number };
  };
}

// ============================================================
// Main AppBouncer Class
// ============================================================

export class AppBouncer {
  private config: AppBouncerConfig;
  private callbacks: AppBouncerCallbacks = {};

  // Modules
  private camera: CameraManager | null = null;
  private ageVerifier: AgeVerifier | null = null;
  private livenessDetector: LivenessDetector | null = null;
  private documentCapture: DocumentCapture | null = null;
  private uiStateMachine: UIStateMachine | null = null;
  private faceDetector: MediaPipeFaceDetector | null = null;
  private faceDetectionLoop: FaceDetectionLoop | null = null;
  private faceComparator: FaceComparator | null = null;
  private requirementsService: RequirementsService | null = null;
  private pollingService: PollingService | null = null;

  // State
  private initialized = false;
  private lastFaceData: FaceLandmarks | null = null;
  private requirements: RequirementsData | null = null;
  private currentSession: VerificationSession | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private overlayCanvas: HTMLCanvasElement | null = null;
  private isLivenessRunning = false;

  // Face blob captured during age estimation (used for document verification)
  private capturedFaceBlob: Blob | null = null;

  // Reason-based pause tracking for liveness (prevents one resume cancelling another's pause)
  private livenessPauseReasons: Set<string> = new Set();

  // Age estimation pause state (paused when face leaves oval)
  private isAgePaused = false;

  constructor(config: AppBouncerConfig) {
    this.config = {
      mode: 'full',
      debug: false,
      ...config,
    };

    this.validateConfig();
  }

  private validateConfig(): void {
    if (!this.config.apiBaseUrl) {
      throw new Error('AppBouncer: apiBaseUrl is required');
    }
  }

  // ============================================================
  // Public API - Initialization
  // ============================================================

  /**
   * Initialize the SDK with all modules
   */
  async initialize(
    videoElement: HTMLVideoElement,
    overlayCanvas?: HTMLCanvasElement
  ): Promise<void> {
    if (this.initialized) {
      this.log('SDK already initialized');
      return;
    }

    this.videoElement = videoElement;
    this.overlayCanvas = overlayCanvas || null;

    try {
      // Initialize camera with video element attached separately
      this.camera = new CameraManager({
        ...this.config.camera,
      });

      // Initialize MediaPipe face detector
      this.faceDetector = new MediaPipeFaceDetector();
      await this.faceDetector.initialize();
      this.log('MediaPipe face detector initialized');

      // Initialize face detection loop (will be configured later with video element)
      // Note: We defer full initialization until start() when videoElement is available

      // Initialize requirements service (for dynamic flow building)
      this.requirementsService = new RequirementsService({
        apiBaseUrl: this.config.apiBaseUrl,
      });

      // Initialize polling service (for OCR job status)
      this.pollingService = new PollingService({
        apiBaseUrl: this.config.apiBaseUrl,
      });

      // Initialize age verifier with correct model path
      // If assetsBaseUrl is provided, build absolute URL for the model
      // Note: Only pass debug:true if explicitly enabled, otherwise let AgeVerifier use its default
      const ageVerifierConfig: Partial<AgeVerifierConfig> = {
        ...this.config.ageVerifier,
        ...(this.config.debug ? { debug: true } : {}),
      };
      if (this.config.assetsBaseUrl && !ageVerifierConfig.modelPath) {
        const baseUrl = this.config.assetsBaseUrl.endsWith('/')
          ? this.config.assetsBaseUrl.slice(0, -1)
          : this.config.assetsBaseUrl;
        ageVerifierConfig.modelPath = `${baseUrl}/models/model.onnx`;
        this.log(`Using model path: ${ageVerifierConfig.modelPath}`);
      }
      this.ageVerifier = new AgeVerifier(ageVerifierConfig);

      // Initialize liveness detector (offline mode - no API calls)
      this.livenessDetector = new LivenessDetector({
        actionCount: 1, // Default to 1; backend may override via liveness_action_count
        maxAttempts: 3,
        debug: this.config.debug,
        ...this.config.liveness,
      });

      // Set liveness callbacks with real MediaPipe face detection
      // Note: getLandmarks() reads from lastFaceData, which is populated by
      // checkFacePosition() called from the controller's face detection loop.
      // This works even when the SDK's internal FaceDetectionLoop isn't started.
      this.livenessDetector.setCallbacks({
        getLandmarks: () => this.convertLandmarks(this.lastFaceData),
        onActionComplete: (result: ActionResult) => {
          this.uiStateMachine?.markActionCompleted(result.action);
        },
        onPrompt: (action, hint, stepIndex, totalSteps) => {
          // Notify UI about new action with step info (for dynamic prompt and progress updates)
          this.callbacks.onLivenessAction?.(action, hint || action, stepIndex, totalSteps);
          this.callbacks.onStatus?.(hint || action, 'info');
        },
      });

      // Initialize document capture with API key for presigned URLs
      this.documentCapture = new DocumentCapture({
        apiBaseUrl: this.config.apiBaseUrl,
        apiKey: this.config.apiKey,
        ...this.config.document,
      });

      // Set document capture callbacks
      this.documentCapture.setCallbacks({
        onCaptureComplete: (doc: CapturedDocument) => {
          this.callbacks.onDocumentCapture?.(doc);
        },
        onStatusMessage: (msg: string) => {
          this.callbacks.onStatus?.(msg, 'info');
        },
      });

      // Initialize UI state machine
      this.uiStateMachine = new UIStateMachine({
        mode: this.config.mode || 'full',
        onScreenChange: (screen, prev) => {
          this.callbacks.onScreenChange?.(screen, prev);
          this.handleScreenChange(screen);
        },
        onComplete: (result) => {
          this.callbacks.onComplete?.(result);
        },
        onError: (error) => {
          this.callbacks.onError?.(error, 'ui');
        },
        onStatusMessage: (msg, type) => {
          this.callbacks.onStatus?.(msg, type);
        },
        ...this.config.ui,
      });

      // Initialize age model
      await this.ageVerifier.initialize();

      // Initialize face comparator (for anti-spoofing consistency check)
      // Reset to ensure clean state for new verification
      this.faceComparator = resetFaceComparator();
      this.log('Face comparator initialized');

      this.initialized = true;
      this.log('SDK initialized successfully');
      this.callbacks.onReady?.();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'initialization');
      throw err;
    }
  }

  /**
   * Set callbacks after initialization
   */
  setCallbacks(callbacks: AppBouncerCallbacks): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  // ============================================================
  // Public API - Verification Flow
  // ============================================================

  /**
   * Start verification flow
   */
  async start(): Promise<void> {
    this.ensureInitialized();

    // Create new session
    this.currentSession = {
      sessionId: this.generateSessionId(),
      startedAt: Date.now(),
      mode: this.config.mode || 'full',
      results: {},
    };

    // Reset face comparator for clean state
    this.faceComparator = resetFaceComparator();

    this.uiStateMachine!.start();
    this.log('Verification flow started', this.currentSession);
  }

  /**
   * Fetch requirements from backend and set up dynamic flow.
   * Call this after initialization to configure the wizard based on user's country and tenant rules.
   *
   * @param apiKey - The API key for authentication (uses config.apiKey if not provided)
   * @param userId - Optional external user identifier for tracking
   * @returns The requirements data from the backend
   */
  async fetchRequirements(apiKey?: string, userId?: string | null): Promise<RequirementsData> {
    this.ensureInitialized();

    if (!this.requirementsService) {
      throw new Error('Requirements service not initialized');
    }

    const key = apiKey || this.config.apiKey;
    if (!key) {
      throw new Error('API key is required to fetch requirements');
    }

    try {
      this.log('Fetching verification requirements...');
      const requirements = await this.requirementsService.checkRequirements(key, userId);
      this.requirements = requirements;

      // Set up the dynamic flow based on returned methods
      const methods = requirements.methods as VerificationMethod[];
      this.uiStateMachine!.setFlowFromMethods(methods);

      // Apply backend-driven liveness action count
      if (requirements.liveness_action_count && this.livenessDetector) {
        this.livenessDetector.setActionCount(requirements.liveness_action_count);
      }

      this.log('Requirements loaded:', {
        country: requirements.country,
        regime: requirements.regime,
        methods: requirements.methods,
        flow: this.uiStateMachine!.getFlowScreens(),
      });

      this.callbacks.onStatus?.(
        `Verification configured for ${requirements.country_name || requirements.country}`,
        'info'
      );

      return requirements;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'requirements');
      throw err;
    }
  }

  /**
   * Get the current requirements (if already fetched)
   */
  getRequirements(): RequirementsData | null {
    return this.requirements;
  }

  /**
   * Check if a specific verification method is required
   */
  isMethodRequired(method: VerificationMethod): boolean {
    return this.uiStateMachine?.isMethodRequired(method) ?? false;
  }

  /**
   * Manually set the verification flow from methods.
   * Use this when requirements were fetched externally (e.g., by the HTML)
   * but the SDK's fetchRequirements() failed.
   *
   * @param methods - Array of verification method names
   */
  setFlowFromMethods(methods: VerificationMethod[]): void {
    if (!this.uiStateMachine) {
      throw new Error('SDK not initialized. Call initialize() first.');
    }
    this.uiStateMachine.setFlowFromMethods(methods);
    this.log('Flow set manually from methods:', methods);
    this.log('Flow screens:', this.uiStateMachine.getFlowScreens());
  }

  /**
   * Get required methods (if any were loaded)
   */
  getRequiredMethods(): VerificationMethod[] {
    return this.uiStateMachine?.getState().requiredMethods ?? [];
  }

  // ============================================================
  // Public API - State Setters (for age metadata tracking)
  // ============================================================

  /**
   * Set whether age verification passed (from ONNX model)
   */
  setAgeVerified(verified: boolean): void {
    this.uiStateMachine?.setAgeVerified(verified);
  }

  /**
   * Set the OCR-extracted age from document
   */
  setOcrAge(age: number): void {
    this.uiStateMachine?.setOcrAge(age);
  }

  /**
   * Set the ONNX-estimated age
   */
  setEstimatedAge(age: number): void {
    this.uiStateMachine?.setEstimatedAge(age);
  }

  /**
   * Set the best image for upload
   */
  setBestImage(imageDataUrl: string | null): void {
    this.uiStateMachine?.setBestImage(imageDataUrl);
  }

  /**
   * Set consent status for data collection
   */
  setConsentGiven(consent: boolean | null): void {
    this.uiStateMachine?.setConsentGiven(consent);
  }

  /**
   * Get the current UI state data
   */
  getUIState(): ReturnType<UIStateMachine['getState']> | null {
    return this.uiStateMachine?.getState() ?? null;
  }

  /**
   * Request camera permission and start stream
   */
  async requestCameraAccess(): Promise<boolean> {
    this.ensureInitialized();

    try {
      await this.camera!.start(this.videoElement!);
      this.callbacks.onStatus?.(t('status.cameraStarted', 'Camera started'), 'success');
      return true;
    } catch (error) {
      const cameraError = error as CameraErrorInfo;
      this.callbacks.onError?.(new Error(cameraError.message), 'camera');
      return false;
    }
  }

  /**
   * Perform face positioning check using MediaPipe
   */
  async checkFacePosition(): Promise<boolean> {
    this.ensureInitialized();

    if (!this.videoElement) {
      throw new Error('Video element not available');
    }

    if (!this.faceDetector) {
      throw new Error('Face detector not initialized');
    }

    try {
      const faceData = await this.faceDetector.detectFace(this.videoElement);
      this.lastFaceData = faceData;

      if (!faceData) {
        this.callbacks.onFaceDetected?.(false);
        this.callbacks.onStatus?.(t('status.noFaceDetected', 'No face detected'), 'warning');
        return false;
      }

      // Check scene quality - convert landmarks to face-utils format
      const landmarks = this.convertLandmarks(faceData);
      if (landmarks && landmarks.length > 0) {
        const quality = checkSceneQuality(this.videoElement, landmarks, {
          containerWidth: 260,
          containerHeight: 340,
        });

        // Use centralized face quality feedback
        const feedback = getFeedbackFromQuality(quality, 'detailed');
        this.callbacks.onStatus?.(feedback.message, feedback.type);
        this.callbacks.onFaceDetected?.(quality.faceDetected, quality);

        // Check if face is properly positioned (includes oval check)
        const isPositioned =
          quality.faceDetected &&
          !quality.faceTooSmall &&
          !quality.faceTooLarge &&
          quality.faceInCenter &&
          quality.faceInsideOval;

        // Capture selfie for anti-spoofing when face is properly positioned
        // Only capture once (check if not already captured at this stage)
        if (isPositioned && !this.faceComparator?.hasCaptured('face_position')) {
          this.captureSelfieForComparison('face_position');
        }

        return isPositioned;
      }

      return true;
    } catch (error) {
      this.log('Face position check error:', error);
      return false;
    }
  }

  /**
   * Ensure session exists, creating one if needed
   */
  private ensureSession(): void {
    if (!this.currentSession) {
      this.log('Auto-creating session');
      this.currentSession = {
        sessionId: this.generateSessionId(),
        startedAt: Date.now(),
        mode: this.config.mode || 'full',
        results: {},
      };
    }
  }

  /**
   * Run liveness verification (offline - no API calls)
   * Picks 2 random actions and runs them locally
   */
  async runLiveness(): Promise<boolean> {
    // Prevent re-entry from circular screen change
    if (this.isLivenessRunning) {
      this.log('runLiveness: Already running, ignoring duplicate call');
      return false;
    }
    this.isLivenessRunning = true;

    this.ensureInitialized();
    this.ensureSession();

    this.log('runLiveness: Starting liveness verification');
    this.log('runLiveness: faceDetectionLoop active:', !!this.faceDetectionLoop?.isActive());

    // Relax oval check during liveness (allows natural head turns for look left/right)
    this.faceDetectionLoop?.setOvalRadiusMultiplier(1.3);

    try {
      this.uiStateMachine!.goTo('liveness_check');

      // Run liveness verification (offline - picks random actions)
      this.log('runLiveness: Calling livenessDetector.verify()');
      const result: LivenessVerifyResult = await this.livenessDetector!.verify();
      this.log('runLiveness: Result:', result.success, 'actions:', result.actions);

      // Store liveness results in session (including individual action results)
      const passedCount = result.results.filter((r) => r.ok).length;
      const totalCount = result.results.length;

      this.currentSession!.results.liveness = {
        passed: result.success,
        score: passedCount / totalCount, // Simple score: passed actions / total
        actions: result.results.map((r) => ({ action: r.action, ok: r.ok })),
      };

      if (result.success) {
        // Capture selfie for anti-spoofing after liveness passes
        this.captureSelfieForComparison('liveness');

        this.uiStateMachine!.goTo('liveness_complete');
        this.callbacks.onStatus?.(t('status.livenessVerified', 'Liveness verified'), 'success');
        return true;
      } else {
        this.callbacks.onStatus?.(
          t('status.livenessCheckFailed', 'Liveness check failed'),
          'error'
        );
        return false;
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'liveness');
      return false;
    } finally {
      this.isLivenessRunning = false;
      // Restore strict oval check after liveness
      this.faceDetectionLoop?.setOvalRadiusMultiplier(1.0);
    }
  }

  /**
   * Verify age from current frame (single verification)
   * Returns verification result with probabilities for each threshold.
   * Also derives an estimated age for backward compatibility with callbacks.
   */
  async estimateAge(): Promise<AgeVerificationResult | null> {
    this.ensureInitialized();

    if (!this.videoElement) {
      throw new Error('Video element not available');
    }

    try {
      // Get landmarks from last face detection
      const landmarks = this.lastFaceData?.landmarks;
      if (!landmarks || landmarks.length === 0) {
        this.log('No face landmarks available for age verification');
        return null;
      }

      // Convert landmarks to the format expected by AgeVerifier
      // MediaPipe landmarks are [x, y, z] arrays, convert to {x, y} objects
      const landmarkPoints = landmarks.map((lm: number[]) => ({ x: lm[0], y: lm[1] }));

      // Run verification using backend min_age (default 18)
      const minAge = (this.uiStateMachine?.getState()?.minAge ?? 18) as 12 | 15 | 18 | 21 | 25;
      const result = await this.ageVerifier!.verifyFromVideo(
        this.videoElement,
        landmarkPoints,
        minAge
      );

      if (result) {
        // Derive estimated age from probabilities for backward compatibility
        const estimatedAge = this.estimateAgeFromProbabilities(result.allProbabilities);

        this.currentSession!.results.age = {
          estimate: estimatedAge,
          confidence: result.confidence,
        };
        this.callbacks.onAgeEstimate?.(estimatedAge, result.confidence);
      }

      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'age_estimation');
      return null;
    }
  }

  /**
   * Estimate age from threshold probabilities using linear interpolation.
   * Maps probabilities to approximate age for backward compatibility.
   */
  private estimateAgeFromProbabilities(probs: Record<string, number>): number {
    // Threshold midpoints for interpolation
    const thresholds = [
      { age: 12, key: '12+' },
      { age: 15, key: '15+' },
      { age: 18, key: '18+' },
      { age: 21, key: '21+' },
      { age: 25, key: '25+' },
    ];

    // Find where probability drops below 50% (transition point)
    for (let i = 0; i < thresholds.length; i++) {
      const prob = probs[thresholds[i].key] ?? 0;
      if (prob < 0.5) {
        if (i === 0) return 10; // Very young
        // Interpolate between this threshold and the previous one
        const prevProb = probs[thresholds[i - 1].key] ?? 1;
        const ratio = (0.5 - prob) / (prevProb - prob);
        return thresholds[i - 1].age + ratio * (thresholds[i].age - thresholds[i - 1].age);
      }
    }

    // All probabilities >= 50%, estimate based on 25+ probability
    const prob25 = probs['25+'] ?? 0.5;
    // Map 50-100% probability to ages 25-40
    return 25 + (prob25 - 0.5) * 30;
  }

  /**
   * Verify age with multiple samples from live video.
   *
   * This method captures fresh frames for each verification, addressing the bug
   * where the same image was used for all verifications.
   *
   * Flow:
   * 1. Wait for 3-second countdown (user positions face)
   * 2. For each of 8 verifications: get fresh landmarks, crop face, run inference
   * 3. Aggregate using 80% pass rate (8/10 must pass with >= 98% confidence)
   *
   * @param config - Multi-verification configuration
   * @returns Aggregated verification result with pass rate info
   */
  async estimateAgeMultiple(config?: MultiVerifyConfig): Promise<MultiVerificationResult | null> {
    this.ensureInitialized();
    this.ensureSession();

    if (!this.videoElement) {
      throw new Error('Video element not available');
    }

    try {
      // Initial landmark check - we need at least one valid detection to proceed
      if (!this.lastFaceData?.landmarks || this.lastFaceData.landmarks.length === 0) {
        this.log('No face landmarks available for age verification');
        return null;
      }

      // Keep lastFaceData fresh during age captures.
      // The main face detection loop only runs on face_position/liveness screens,
      // so we start a dedicated interval here to detect when the user moves away.
      const ageDetectionInterval = setInterval(async () => {
        try {
          if (this.faceDetector && this.videoElement) {
            const faceData = await this.faceDetector.detectFace(this.videoElement);
            this.lastFaceData = faceData; // null if no face → getLandmarks() returns null → frame skipped

            // Scene quality pause for age estimation (oval + face scale)
            if (faceData) {
              const landmarks = this.convertLandmarks(faceData);
              if (landmarks && landmarks.length > 0) {
                const quality = checkSceneQuality(this.videoElement!, landmarks, {
                  containerWidth: 260,
                  containerHeight: 340,
                });
                const shouldPause =
                  !quality.faceInsideOval || quality.faceTooSmall || quality.faceTooLarge;
                if (shouldPause && !this.isAgePaused) {
                  this.pauseAge();
                  // Fire onFaceDetected so UI shows the correct warning
                  this.callbacks.onFaceDetected?.(true, quality);
                } else if (!shouldPause && this.isAgePaused) {
                  this.resumeAge();
                  this.callbacks.onFaceDetected?.(true, quality);
                  this.callbacks.onStatus?.(t('status.verifyingAge', 'Verifying age...'), 'info');
                }
              } else if (!this.isAgePaused) {
                this.pauseAge();
                this.callbacks.onStatus?.(
                  t('status.noFaceDetected', 'No face detected'),
                  'warning'
                );
              }
            } else if (!this.isAgePaused) {
              this.pauseAge();
              this.callbacks.onStatus?.(t('status.noFaceDetected', 'No face detected'), 'warning');
            }
          }
        } catch {
          // Non-critical — log but don't break the interval
          this.log('Face detection during age estimation failed');
        }
      }, 100); // 10 FPS — sufficient for 600ms capture intervals

      // Build config for live multi-verification
      // This method captures fresh frames for each verification
      const liveConfig: LiveMultiVerifyConfig = {
        ...config,
        captureImages: true, // Always capture images for best-image selection
        countdownSeconds: 3, // Give user time to position face
        onCountdown: (secondsRemaining) => {
          // Notify UI about countdown
          if (secondsRemaining > 0) {
            this.callbacks.onStatus?.(
              t('face.holdStillCountdown', 'Hold still... {count}').replace(
                '{count}',
                String(secondsRemaining)
              ),
              'info'
            );
          } else {
            this.callbacks.onStatus?.(t('status.verifyingAge', 'Verifying age...'), 'info');
          }
        },
        onProgress: (current, total) => {
          // Call user's callback if provided
          config?.onProgress?.(current, total);
        },
        // This callback fetches fresh landmarks for each verification
        getLandmarks: () => {
          // Skip frame when age estimation is paused (face outside oval)
          if (this.isAgePaused) return null;
          // Use lastFaceData which is updated by checkFacePosition()
          // The UI should be calling checkFacePosition() in a loop during verification
          if (!this.lastFaceData?.landmarks || this.lastFaceData.landmarks.length === 0) {
            return null;
          }
          // Convert [x, y, z] arrays to {x, y} objects
          return this.lastFaceData.landmarks.map((lm: number[]) => ({ x: lm[0], y: lm[1] }));
        },
      };

      // Use the new method that captures fresh frames for each verification
      let result: MultiVerificationResult | null;
      try {
        const multiMinAge = (this.uiStateMachine?.getState()?.minAge ?? 18) as
          | 12
          | 15
          | 18
          | 21
          | 25;
        result = await this.ageVerifier!.verifyMultipleFromVideo(
          this.videoElement,
          multiMinAge,
          liveConfig
        );
      } finally {
        clearInterval(ageDetectionInterval);
      }

      if (result) {
        // Derive estimated age from probabilities for backward compatibility
        const estimatedAge = this.estimateAgeFromProbabilities(result.allProbabilities);

        this.currentSession!.results.age = {
          estimate: estimatedAge,
          confidence: result.confidence,
        };
        this.callbacks.onAgeEstimate?.(estimatedAge, result.confidence);

        // Persist age gate decision for goNext() to use
        this.uiStateMachine?.setAgeVerified(result.verified);
        this.uiStateMachine?.setAgeConfidence(result.confidence);

        // Capture selfie for anti-spoofing after age estimation
        this.captureSelfieForComparison('age_estimation');

        // Log pass rate info for debugging
        this.log('Age verification result:', {
          verified: result.verified,
          passRate: result.passRate,
          passedCount: result.passedCount,
          estimatesUsed: result.estimatesUsed,
          avgConfidence: result.confidence,
        });
      }

      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'age_estimation');
      return null;
    }
  }

  /**
   * Upload the best age estimation image to the backend with age metadata.
   * The filename will be: {api_key}/SD{claimed}_OCR{ocr}_ES{estimated}.jpg
   *
   * @param imageDataUrl - The image data URL (JPEG) to upload
   * @returns Upload result with the stored image URL
   */
  async uploadAgeImage(imageDataUrl: string): Promise<{ url: string } | null> {
    this.ensureInitialized();

    if (!imageDataUrl) {
      this.log('No image to upload');
      return null;
    }

    // Get age values from state machine (handle NaN/undefined/null)
    const state = this.uiStateMachine?.getState();
    const ocrAge = Number.isFinite(state?.ocrAge) ? state!.ocrAge : 0;
    const estimatedAge = Number.isFinite(state?.estimatedAge) ? state!.estimatedAge : 0;

    try {
      // Convert data URL to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();

      // Store the face blob for document verification
      this.capturedFaceBlob = blob;

      // Create form data with age metadata
      const formData = new FormData();
      formData.append('file', blob, 'age_estimation.jpg');
      formData.append('api_key', this.config.apiKey || '');
      formData.append('claimed_age', '0');
      formData.append('ocr_age', String(ocrAge));
      formData.append('estimated_age', String(estimatedAge));

      this.log('Uploading age image with metadata:', {
        apiKey: this.config.apiKey,
        ocrAge,
        estimatedAge,
      });

      // Upload to backend
      const uploadResponse = await fetch(`${this.config.apiBaseUrl}/v1/age/upload`, {
        method: 'POST',
        headers: {
          ...(this.config.apiKey && { 'X-API-Key': this.config.apiKey }),
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(errorData.error || 'Failed to upload age image');
      }

      const data = await uploadResponse.json();
      this.log('Age image uploaded:', data.url);
      return { url: data.url };
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'age_upload');
      return null;
    }
  }

  /**
   * Capture document (front or back)
   */
  async captureDocument(side: 'front' | 'back'): Promise<CapturedDocument | null> {
    this.ensureInitialized();

    if (!this.videoElement) {
      throw new Error('Video element not available');
    }

    try {
      const doc = await this.documentCapture!.captureFromVideo(this.videoElement, side);

      if (doc) {
        this.uiStateMachine!.markDocumentCaptured(side);
      }

      return doc;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'document_capture');
      return null;
    }
  }

  /**
   * Upload captured documents and poll for OCR results
   * Uses presigned URLs for direct S3 upload, then calls verify-urls endpoint
   */
  async uploadDocuments(): Promise<boolean> {
    this.ensureInitialized();

    try {
      this.uiStateMachine!.goTo('document_processing');

      // Pass the captured face blob to document capture before uploading
      // This is required for face-document matching during OCR verification
      if (this.capturedFaceBlob) {
        this.documentCapture!.setFaceImage(this.capturedFaceBlob);
        this.log('Face image set for document verification');
      } else {
        this.log('Warning: No face image available for document verification');
      }

      // Pass verification session ID if available from requirements
      if (this.requirements?.session_id) {
        this.documentCapture!.setVerificationSessionId(this.requirements.session_id);
        this.log('Verification session ID set:', this.requirements.session_id);
      }

      // Pass age metadata for selfie naming: sd0_es{Y}_ocr0.jpg
      // - sd = always 0 (self-declaration removed)
      // - es = estimated age (0 if age estimation not done or not required)
      // - ocr = 0 initially, backend updates to actual age after OCR processing
      const uiState = this.uiStateMachine!.getState();
      const estimatedAge = Number.isFinite(uiState.estimatedAge) ? uiState.estimatedAge : 0;

      this.documentCapture!.setAgeMetadata(0, estimatedAge);
      this.log('Age metadata set for selfie naming:', { estimatedAge });

      // Upload documents using presigned URLs (direct to S3)
      await this.documentCapture!.uploadDocuments();

      // Check result from document capture state
      const state = this.documentCapture!.getState();
      const verified = state === 'success';

      this.currentSession!.results.document = {
        verified,
        extractedData: {},
      };

      return verified;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'document_upload');
      return false;
    }
  }

  /**
   * Poll for OCR job completion.
   * Use this after submitting a document for OCR processing.
   *
   * @param taskId - The task ID returned from the OCR submission
   * @returns The OCR result when completed
   */
  async pollForOcrResult<T = unknown>(taskId: string): Promise<T> {
    this.ensureInitialized();

    if (!this.pollingService) {
      throw new Error('Polling service not initialized');
    }

    try {
      this.callbacks.onStatus?.(t('status.processingDocument', 'Processing document...'), 'info');

      const result = await this.pollingService.pollForResult<T>(taskId, '/v1/ocr/status', {
        apiKey: this.config.apiKey,
        callbacks: {
          onProgress: (progress) => {
            this.uiStateMachine?.setProgress(progress);
          },
          onStatusChange: (status) => {
            this.log('OCR job status:', status);
          },
        },
      });

      this.callbacks.onStatus?.(
        t('status.documentProcessed', 'Document processed successfully'),
        'success'
      );
      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err, 'ocr_polling');
      throw err;
    }
  }

  /**
   * Complete verification and submit to server
   */
  async completeVerification(): Promise<VerificationResult> {
    this.ensureInitialized();

    if (!this.currentSession) {
      throw new Error('No active session');
    }

    try {
      this.uiStateMachine!.goTo('processing');

      // Run face comparison (anti-spoofing check) — hard gate on failure
      const faceComparisonResult = this.runFaceComparison();
      if (faceComparisonResult && !faceComparisonResult.passed) {
        this.log('Face comparison FAILED — face swap detected between verification stages');
        this.uiStateMachine!.goTo('failure');
        this.callbacks.onError?.(
          new Error('Face mismatch between verification stages'),
          'face_comparison'
        );
        return {
          success: false,
          sessionId: this.currentSession.sessionId,
          faceMatch: false,
          errors: ['Face mismatch detected between verification stages'],
        };
      }

      // Submit final verification to server
      const response = await fetch(`${this.config.apiBaseUrl}/v1/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(this.config.apiKey && { 'X-API-Key': this.config.apiKey }),
        },
        body: JSON.stringify({
          session_id: this.currentSession.sessionId,
          challenge_id: this.currentSession.challengeId,
          token: this.currentSession.token,
          results: this.currentSession.results,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Verification failed');
      }

      const data = await response.json();

      const result: VerificationResult = {
        success: data.success,
        sessionId: this.currentSession.sessionId,
        livenessScore: this.currentSession.results.liveness?.score,
        ageEstimate: this.currentSession.results.age?.estimate,
        documentVerified: this.currentSession.results.document?.verified,
        faceMatch: this.currentSession.results.faceMatch?.matched,
        errors: data.errors,
      };

      this.uiStateMachine!.complete(result);
      return result;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      const result: VerificationResult = {
        success: false,
        sessionId: this.currentSession.sessionId,
        errors: [err.message],
      };
      this.uiStateMachine!.complete(result);
      return result;
    }
  }

  /**
   * Cancel verification
   */
  cancel(): void {
    this.cleanup();
    this.currentSession = null;
    this.isAgePaused = false;
    this.uiStateMachine?.reset();
    // Reset face comparator for potential retry
    this.faceComparator = resetFaceComparator();
    this.callbacks.onStatus?.(t('status.verificationCancelled', 'Verification cancelled'), 'info');
  }

  /**
   * Dispose SDK and release resources
   */
  dispose(): void {
    this.cleanup();

    // Dispose face detection loop
    this.faceDetectionLoop?.dispose();
    this.faceDetectionLoop = null;

    // Dispose face detector
    this.faceDetector?.dispose();
    this.faceDetector = null;

    // Dispose polling service (cancel any active polls)
    this.pollingService?.cancelAll();
    this.pollingService = null;

    // Clear other modules
    this.camera = null;
    this.ageVerifier = null;
    this.livenessDetector = null;
    this.documentCapture = null;
    this.uiStateMachine = null;
    this.requirementsService = null;

    // Clear state
    this.lastFaceData = null;
    this.requirements = null;

    this.initialized = false;
    this.log('SDK disposed');
  }

  // ============================================================
  // Public API - Utilities
  // ============================================================

  /**
   * Get current session
   */
  getSession(): VerificationSession | null {
    return this.currentSession ? { ...this.currentSession } : null;
  }

  /**
   * Get current screen
   */
  getCurrentScreen(): ScreenConfig | null {
    return this.uiStateMachine?.getCurrentScreen() || null;
  }

  /**
   * Get the liveness detector instance (for mock backend mode)
   */
  getLivenessDetector(): LivenessDetector | null {
    return this.livenessDetector;
  }

  /**
   * Get the age verifier instance (for debugging and testing)
   */
  getAgeVerifier(): AgeVerifier | null {
    return this.ageVerifier;
  }

  /**
   * Get the current face landmarks (for debugging)
   * Returns an array of {x, y} coordinates (468 points from MediaPipe)
   */
  getLastFaceLandmarks(): Array<{ x: number; y: number }> | null {
    if (!this.lastFaceData?.landmarks) {
      return null;
    }
    return this.lastFaceData.landmarks.map((lm) => ({
      x: lm[0],
      y: lm[1],
    }));
  }

  /**
   * Get the video element (for debugging)
   */
  getVideoElement(): HTMLVideoElement | null {
    return this.videoElement;
  }

  /**
   * Pause liveness detection for a given reason.
   * Multiple reasons can be active simultaneously — only resumes when all are cleared.
   */
  pauseLiveness(reason: string = 'lighting'): void {
    this.livenessPauseReasons.add(reason);
    this.livenessDetector?.pause();
  }

  /**
   * Remove a pause reason. Only resumes if no other reasons remain.
   */
  resumeLiveness(reason: string = 'lighting'): void {
    this.livenessPauseReasons.delete(reason);
    if (this.livenessPauseReasons.size === 0) {
      this.livenessDetector?.resume();
    }
  }

  /**
   * Check if liveness is paused for a specific reason.
   */
  isLivenessPausedForReason(reason: string): boolean {
    return this.livenessPauseReasons.has(reason);
  }

  /**
   * Check if liveness is paused for ANY reason.
   */
  isLivenessPaused(): boolean {
    return this.livenessPauseReasons.size > 0;
  }

  /**
   * Reset the liveness detector state.
   * Call this before retrying liveness verification.
   */
  resetLivenessDetector(): void {
    if (this.livenessDetector) {
      this.livenessDetector.reset();
      this.log('Liveness detector reset');
    }
    this.livenessPauseReasons.clear();
    // Also clear liveness results from session
    if (this.currentSession?.results) {
      this.currentSession.results.liveness = undefined;
    }
  }

  /**
   * Get the liveness result from the current session.
   * Returns null if no liveness check has been performed.
   *
   * @returns Liveness result with passed status, score, and action results
   */
  getLivenessResult(): VerificationSession['results']['liveness'] | null {
    return this.currentSession?.results.liveness ?? null;
  }

  /**
   * Pause age estimation (e.g. face outside oval).
   * getLandmarks() will return null, causing the current frame to be skipped.
   */
  private pauseAge(): void {
    this.isAgePaused = true;
    this.log('Age estimation paused - face outside oval');
  }

  /**
   * Resume age estimation after face returns to oval.
   */
  private resumeAge(): void {
    this.isAgePaused = false;
    this.log('Age estimation resumed');
  }

  /**
   * Navigate to specific screen
   */
  goToScreen(screen: ScreenName): void {
    this.ensureInitialized();
    this.uiStateMachine!.goTo(screen);
  }

  /**
   * Go back to previous screen
   */
  goBack(): void {
    this.ensureInitialized();
    this.uiStateMachine!.back();
  }

  /**
   * Go to next screen.
   * Implements age gate: if on liveness_complete, checks ageVerified to decide
   * whether to skip document upload (success path) or proceed to OCR screens.
   */
  goNext(): void {
    this.ensureInitialized();

    // Age gate decision point - uses 99% confidence threshold
    if (this.isAtAgeGateDecision()) {
      // Check if age is verified with high confidence (99%+)
      if (this.uiStateMachine!.isAgeVerifiedWithHighConfidence()) {
        this.log('Age gate: HIGH confidence (99%+), skipping OCR - going directly to processing');
        // Skip document consent and OCR entirely - nothing to upload
        this.uiStateMachine!.skipToProcessing();
        return;
      }

      // Age not verified with high confidence - need OCR
      // First, show document consent modal to ask for permission
      this.log('Age gate: LOW confidence, proceeding to document_consent for OCR flow');
      // The flow will continue to document_consent screen
    }

    this.uiStateMachine!.next();
  }

  /**
   * Check if we're at the age gate decision point.
   * Returns true when:
   * - Current screen is liveness_complete
   * - OCR is configured (either 'ocr' or 'ocr_fallback')
   * - Next screen would be document-related
   *
   * The actual decision (skip OCR or show consent modal) is made in goNext()
   * based on the age confidence threshold (99%).
   */
  private isAtAgeGateDecision(): boolean {
    const currentScreen = this.uiStateMachine!.getCurrentScreen().name;
    if (currentScreen !== 'liveness_complete') {
      return false;
    }

    // Apply age gate for both 'ocr' and 'ocr_fallback'
    // The 99% confidence threshold determines if we can skip
    const hasOcr =
      this.uiStateMachine!.isMethodRequired('ocr') ||
      this.uiStateMachine!.isMethodRequired('ocr_fallback');

    if (!hasOcr) {
      return false;
    }

    // Don't gate if 'ocr' is also required (country mandates OCR)
    if (this.uiStateMachine!.isMethodRequired('ocr')) {
      return false;
    }

    return this.uiStateMachine!.isNextScreenOcr();
  }

  /**
   * Capture frame as ImageData
   */
  captureFrame(): ImageData | null {
    this.ensureInitialized();
    return this.camera!.captureFrame();
  }

  /**
   * Get camera stream status
   */
  isCameraActive(): boolean {
    return this.camera?.getState() === 'streaming';
  }

  // ============================================================
  // Private Methods
  // ============================================================

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error('SDK not initialized. Call initialize() first.');
    }
  }

  private handleScreenChange(screen: ScreenConfig): void {
    // Handle automatic actions on screen transitions
    switch (screen.name) {
      case 'permissions':
        // Auto-request camera on permissions screen
        this.requestCameraAccess().then((granted) => {
          if (granted) {
            this.uiStateMachine!.next();
          }
        });
        break;

      case 'face_position':
        // Start face detection loop using extracted module
        this.startFaceDetectionLoop();
        break;

      // NOTE: liveness_check case removed to prevent circular call chain
      // runLiveness() navigates to liveness_check which would re-trigger runLiveness()

      default:
        break;
    }
  }

  /**
   * Initialize and start the face detection loop
   */
  private startFaceDetectionLoop(): void {
    if (!this.videoElement || !this.faceDetector) {
      this.log('Cannot start face detection loop: missing video element or face detector');
      return;
    }

    // Create loop if not exists
    if (!this.faceDetectionLoop) {
      this.faceDetectionLoop = new FaceDetectionLoop({
        faceDetector: this.faceDetector,
        videoElement: this.videoElement,
        debug: this.config.debug,
        containerWidth: 260,
        containerHeight: 340,
      });

      // Set callbacks
      this.faceDetectionLoop.setCallbacks({
        onFaceDetected: (detected, quality) => {
          this.callbacks.onFaceDetected?.(detected, quality);
        },
        shouldContinue: () => {
          // Continue loop on face position and liveness screens
          const currentScreen = this.uiStateMachine?.getCurrentScreen().name;
          return (
            currentScreen === 'face_position' ||
            currentScreen === 'liveness_check' ||
            currentScreen === 'liveness_action'
          );
        },
        onError: (error) => {
          this.log('Face detection error:', error);
        },
      });
    }

    this.faceDetectionLoop.start();
  }

  /**
   * Convert FaceLandmarks to face-utils Landmark format
   */
  private convertLandmarks(faceData: FaceLandmarks | null): Landmark[] | null {
    if (!faceData) return null;
    return faceData.landmarks.map(([x, y, z]) => ({ x, y, z: z || 0 }));
  }

  private cleanup(): void {
    // Stop face detection loop
    this.faceDetectionLoop?.stop();

    this.camera?.stop();
    this.documentCapture?.reset();
    this.isAgePaused = false;
  }

  private generateSessionId(): string {
    return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Capture selfie for face comparison anti-spoofing check.
   * Uses current face landmarks from lastFaceData.
   *
   * @param stage - The verification stage to capture at
   * @returns true if capture was successful
   */
  private captureSelfieForComparison(stage: CaptureStage): boolean {
    if (!this.faceComparator) {
      this.log('Face comparator not initialized, skipping selfie capture');
      return false;
    }

    const landmarks = this.convertLandmarks(this.lastFaceData);
    if (!landmarks || landmarks.length < 468) {
      this.log(`Cannot capture selfie at ${stage}: no valid landmarks`);
      return false;
    }

    const captured = this.faceComparator.capture(stage, landmarks);
    this.log(`Selfie capture at ${stage}:`, captured ? 'success' : 'failed');
    return captured;
  }

  /**
   * Run face comparison across all captured stages.
   * Should be called before completing verification.
   *
   * @returns Face comparison result
   */
  runFaceComparison(): FaceComparisonResult | null {
    if (!this.faceComparator) {
      this.log('Face comparator not initialized');
      return null;
    }

    const result = this.faceComparator.runComparison();
    this.log('Face comparison result:', {
      passed: result.passed,
      averageSimilarity: result.averageSimilarity.toFixed(3),
      captureCount: result.captureCount,
    });

    // Notify callback
    this.callbacks.onFaceComparison?.(result);

    // Store result in session
    if (this.currentSession) {
      this.currentSession.results.faceMatch = {
        matched: result.passed,
        confidence: result.averageSimilarity,
      };
    }

    return result;
  }

  /**
   * Reset face comparator for a new verification attempt.
   * Call this when restarting the verification flow.
   */
  resetFaceComparator(): void {
    this.faceComparator = resetFaceComparator();
    this.log('Face comparator reset for new verification');
  }

  private log(...args: unknown[]): void {
    if (this.config.debug) {
      // Redact sensitive data (API keys, tokens, etc.) from log output
      const safeArgs = args.map((arg) =>
        typeof arg === 'object' && arg !== null
          ? redactSecrets(arg as Record<string, unknown>)
          : arg
      );
      console.log('[AppBouncer]', ...safeArgs);
    }
  }
}

// ============================================================
// Factory Function
// ============================================================

/**
 * Create and initialize AppBouncer SDK
 */
export async function createAppBouncer(
  config: AppBouncerConfig,
  videoElement: HTMLVideoElement,
  overlayCanvas?: HTMLCanvasElement,
  callbacks?: AppBouncerCallbacks
): Promise<AppBouncer> {
  const sdk = new AppBouncer(config);

  if (callbacks) {
    sdk.setCallbacks(callbacks);
  }

  await sdk.initialize(videoElement, overlayCanvas);
  return sdk;
}
